import React, { useState, useMemo } from 'react';
import './usertools-css/MagnesiumCalculator.css';

// --- Standards & Clinical DRI Constants ---
// Official Dietary Reference Intakes (NIH Office of Dietary Supplements / National Academies)
const MG_RDA_TABLE = {
  '0-6m': { rda: 30, isAI: true, label: 'Infants 0-6 months' },
  '7-12m': { rda: 75, isAI: true, label: 'Infants 7-12 months' },
  '1-3y': { rda: 80, isAI: false, label: 'Children 1-3 years' },
  '4-8y': { rda: 130, isAI: false, label: 'Children 4-8 years' },
  '9-13y': { rda: 240, isAI: false, label: 'Children 9-13 years' },
  '14-18_male': { rda: 410, isAI: false, label: 'Teens 14-18 years (Male)' },
  '14-18_female': { rda: 360, isAI: false, label: 'Teens 14-18 years (Female)' },
  '19-30_male': { rda: 400, isAI: false, label: 'Adults 19-30 years (Male)' },
  '19-30_female': { rda: 310, isAI: false, label: 'Adults 19-30 years (Female)' },
  '31-50_male': { rda: 420, isAI: false, label: 'Adults 31-50 years (Male)' },
  '31-50_female': { rda: 320, isAI: false, label: 'Adults 31-50 years (Female)' },
  '51+_male': { rda: 420, isAI: false, label: 'Adults 51+ years (Male)' },
  '51+_female': { rda: 320, isAI: false, label: 'Adults 51+ years (Female)' },

  // Pregnancy
  'pregnant_14-18': { rda: 400, isAI: false, label: 'Pregnancy 14-18 years' },
  'pregnant_19-30': { rda: 350, isAI: false, label: 'Pregnancy 19-30 years' },
  'pregnant_31-50': { rda: 360, isAI: false, label: 'Pregnancy 31-50 years' },

  // Lactation
  'lactating_14-18': { rda: 360, isAI: false, label: 'Lactation 14-18 years' },
  'lactating_19-30': { rda: 310, isAI: false, label: 'Lactation 19-30 years' },
  'lactating_31-50': { rda: 320, isAI: false, label: 'Lactation 31-50 years' },
};

// Tolerable Upper Intake Level (UL) applies ONLY to supplemental magnesium
const MG_SUPPLEMENTAL_UL = 350; // mg/day elemental Mg from supplements

const ACTIVITY_MULTIPLIERS = {
  low: 1.0,      // Sedentary/low sweat losses
  moderate: 1.10, // 3-6 hours moderate/intense exercise/week (+10%)
  high: 1.20,     // 7+ hours heavy endurance or intense training (+20%)
};

const STRESS_SLEEP_MULTIPLIER = 1.05; // High stress or short sleep increases urinary wasting (+5%)

const DIETARY_PRESETS = {
  low: 160,    // Standard Western diet low in greens/nuts/seeds
  medium: 270, // Moderate whole foods and vegetables
  high: 420,   // High-fiber, whole-food plant-forward intake
};

function pickMgRdaKey(age, sex, pregnant, lactating) {
  if (Number.isNaN(age) || age < 0) return null;

  if (age < 0.5) return '0-6m';
  if (age < 1) return '7-12m';

  if (sex === 'female' && age >= 14 && age <= 50) {
    if (pregnant && !lactating) {
      if (age <= 18) return 'pregnant_14-18';
      if (age <= 30) return 'pregnant_19-30';
      return 'pregnant_31-50';
    }
    if (lactating && !pregnant) {
      if (age <= 18) return 'lactating_14-18';
      if (age <= 30) return 'lactating_19-30';
      return 'lactating_31-50';
    }
  }

  if (age <= 3) return '1-3y';
  if (age <= 8) return '4-8y';
  if (age <= 13) return '9-13y';
  if (age <= 18) return sex === 'male' ? '14-18_male' : '14-18_female';
  if (age <= 30) return sex === 'male' ? '19-30_male' : '19-30_female';
  if (age <= 50) return sex === 'male' ? '31-50_male' : '31-50_female';
  return sex === 'male' ? '51+_male' : '51+_female';
}

function MagnesiumCalculator() {
  // Demographics starting from scratch (empty)
  const [age, setAge] = useState('');
  const [sex, setSex] = useState('');
  const [isPregnant, setIsPregnant] = useState(false);
  const [isLactating, setIsLactating] = useState(false);

  // Lifestyle
  const [activityLevel, setActivityLevel] = useState('low');
  const [stressScore, setStressScore] = useState('5'); // 0-10
  const [sleepHours, setSleepHours] = useState('7');

  // Diet
  const [dietMode, setDietMode] = useState('preset'); // 'preset' | 'custom'
  const [dietPreset, setDietPreset] = useState('');
  const [customDietMg, setCustomDietMg] = useState('');

  // Clinical & Medications
  const [ppiUse, setPpiUse] = useState(false);
  const [diureticUse, setDiureticUse] = useState(false);
  const [giMalabsorption, setGiMalabsorption] = useState(false);
  const [kidneyImpairment, setKidneyImpairment] = useState(false);

  // Supplements
  const [takesSupplement, setTakesSupplement] = useState(false);
  const [supplementDoseMg, setSupplementDoseMg] = useState('');
  const [supplementFrequency, setSupplementFrequency] = useState('daily');

  // --- Dynamic Live Calculation ---
  const results = useMemo(() => {
    const hasAge = parseFloat(age) > 0;
    const hasSex = Boolean(sex);

    // Initial awaiting state when user hasn't entered core demographics
    if (!hasAge && !hasSex) {
      return {
        isAwaiting: true,
        error: 'Enter your age and biological sex in the form on the left to calculate your personalized magnesium status.',
      };
    }
    if (!hasAge) {
      return {
        isAwaiting: false,
        error: 'Please enter a valid age.',
      };
    }
    if (!hasSex) {
      return {
        isAwaiting: false,
        error: 'Please select your biological sex to determine clinical dietary reference intakes (DRIs).',
      };
    }

    const ageNum = parseFloat(age);
    const stressNum = parseFloat(stressScore) || 0;
    const sleepNum = parseFloat(sleepHours) || 7;

    const rdaKey = pickMgRdaKey(ageNum, sex, isPregnant, isLactating);
    const rdaEntry = rdaKey ? MG_RDA_TABLE[rdaKey] : MG_RDA_TABLE['31-50_male'];
    let baseRda = rdaEntry.rda;

    // Lifestyle Personalization (Ages 14+)
    let targetMg = baseRda;
    if (ageNum >= 14) {
      const actMult = ACTIVITY_MULTIPLIERS[activityLevel] || 1.0;
      targetMg *= actMult;

      if (stressNum >= 7 || sleepNum <= 6) {
        targetMg *= STRESS_SLEEP_MULTIPLIER;
      }
      targetMg = Math.round(targetMg);
    }

    // Baseline Dietary Mg Intake
    let rawDietary = 0;
    if (dietMode === 'custom') {
      rawDietary = parseFloat(customDietMg) || 0;
    } else if (dietPreset && DIETARY_PRESETS[dietPreset]) {
      rawDietary = DIETARY_PRESETS[dietPreset];
    }

    // Fractional Absorption Multipliers
    let absorptionFactor = 1.0;
    if (ppiUse) absorptionFactor *= 0.90; // -10% from impaired TRPM6 transport
    if (diureticUse) absorptionFactor *= 0.90; // -10% urinary losses
    if (giMalabsorption) absorptionFactor *= 0.80; // -20% intestinal surface loss
    absorptionFactor = Math.max(0.70, absorptionFactor); // Cap max penalty

    const effectiveDietary = Math.round(rawDietary * absorptionFactor);

    // Supplemental Elemental Mg
    let dailySupplementMg = 0;
    if (takesSupplement) {
      const dose = parseFloat(supplementDoseMg) || 0;
      let freqMultiplier = 1.0;
      if (supplementFrequency === '2x_day') freqMultiplier = 2.0;
      else if (supplementFrequency === 'alternate_day') freqMultiplier = 0.5;
      else if (supplementFrequency === 'weekly') freqMultiplier = 1 / 7;

      dailySupplementMg = Math.round(dose * freqMultiplier);
    }

    const totalIntake = effectiveDietary + dailySupplementMg;
    const gap = targetMg - totalIntake; // positive = deficit, negative = surplus

    // --- Dynamic Badge Status (Green, Orange, Red) ---
    let badgeClass = 'mg-badge-green';
    let badgeLabel = 'Adequate Intake';

    if (kidneyImpairment) {
      badgeClass = 'mg-badge-red';
      badgeLabel = 'Renal Caution · Avoid Supplements';
    } else if (dailySupplementMg > MG_SUPPLEMENTAL_UL) {
      badgeClass = 'mg-badge-red';
      badgeLabel = `Exceeds Supplemental UL (${dailySupplementMg} mg)`;
    } else if (totalIntake < targetMg * 0.55) {
      badgeClass = 'mg-badge-red';
      badgeLabel = `Severe Deficit (-${gap} mg/day)`;
    } else if (gap > 100) {
      badgeClass = 'mg-badge-orange';
      badgeLabel = `Intake Deficit (-${gap} mg/day)`;
    } else if (gap > 25) {
      badgeClass = 'mg-badge-orange';
      badgeLabel = `Borderline Intake (-${gap} mg/day)`;
    } else {
      badgeClass = 'mg-badge-green';
      badgeLabel = gap <= 0 ? 'Optimal · Target Met' : 'Adequate Intake';
    }

    return {
      isAwaiting: false,
      baseRda,
      isAI: rdaEntry.isAI,
      targetMg,
      rawDietary,
      effectiveDietary,
      absorptionFactor,
      dailySupplementMg,
      totalIntake,
      gap,
      badgeClass,
      badgeLabel,
      error: null,
    };
  }, [
    age,
    sex,
    isPregnant,
    isLactating,
    activityLevel,
    stressScore,
    sleepHours,
    dietMode,
    dietPreset,
    customDietMg,
    ppiUse,
    diureticUse,
    giMalabsorption,
    kidneyImpairment,
    takesSupplement,
    supplementDoseMg,
    supplementFrequency,
  ]);

  return (
    <div className="magnesiumCalculator">
      {/* Hero Header */}
      <div className="mg-hero">
        <div>
          <p className="mg-eyebrow">Pure Health Nexus / Micronutrient Tools</p>
          <h1>Fuel your cells.<br />Master your magnesium.</h1>
          <p className="mg-hero-desc">
            Calculate your personalized daily magnesium target based on clinical RDAs, exercise losses, stress, and medications.
          </p>

          {/* AI Promo Callout Banner (Completely Namespaced) */}
          <div className="mg-promo-banner">
            <strong>Unlock Smarter Eating:</strong> Get a complete breakdown of vitamins, minerals, and fats plus a personalized weight-loss plan or nutrition tailored to your training with a single{' '}
            <a href="https://purehealthnexus.com/ai-nutrition-vitamins-minerals" className="mg-promo-link">
              AI scan
            </a>
          </div>
        </div>

        <div className="mg-hero-art" aria-hidden="true">
          <img src="/assets/images/appsmagnesium.webp" alt="" className="mg-art-image" />
          <span className="mg-art-caption">CELLULAR BIOCHEMISTRY</span>
        </div>
      </div>

      <div className="mg-layout">
        {/* Left Column: Form Panel */}
        <div className="mg-panel mg-input-panel">
          <div className="mg-panel-heading">
            <div>
              <p className="mg-eyebrow">01 / Profile</p>
              <h2>Your Details</h2>
              <p>Tailor your intake based on physiology and lifestyle.</p>
            </div>
            <span className="mg-step">01</span>
          </div>

          {/* Demographics */}
          <div className="mg-field-row">
            <label className="mg-field">
              <span className="mg-field-label">Age</span>
              <input
                type="number"
                min="1"
                max="115"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                placeholder="Years (e.g. 32)"
              />
            </label>
            <label className="mg-field">
              <span className="mg-field-label">Biological Sex</span>
              <select value={sex} onChange={(e) => setSex(e.target.value)}>
                <option value="">-- Select Sex --</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </label>
          </div>

          {sex === 'female' && parseFloat(age) >= 14 && parseFloat(age) <= 50 && (
            <div className="mg-field-row">
              <label className="mg-checkbox-label">
                <input
                  type="checkbox"
                  checked={isPregnant}
                  onChange={(e) => {
                    setIsPregnant(e.target.checked);
                    if (e.target.checked) setIsLactating(false);
                  }}
                />
                Currently Pregnant
              </label>
              <label className="mg-checkbox-label">
                <input
                  type="checkbox"
                  checked={isLactating}
                  onChange={(e) => {
                    setIsLactating(e.target.checked);
                    if (e.target.checked) setIsPregnant(false);
                  }}
                />
                Currently Lactating
              </label>
            </div>
          )}

          {/* Lifestyle */}
          <div className="mg-section-label">
            <span>Lifestyle &amp; Training</span>
            <small style={{ fontWeight: 400, color: 'var(--mg-muted)', textTransform: 'none' }}>Losses</small>
          </div>

          <label className="mg-field">
            <span className="mg-field-label">Physical Activity Level</span>
            <select value={activityLevel} onChange={(e) => setActivityLevel(e.target.value)}>
              <option value="low">Low / Sedentary (Standard baseline)</option>
              <option value="moderate">Moderate (3-6 hrs/wk training · +10%)</option>
              <option value="high">High / Endurance (7+ hrs/wk training · +20%)</option>
            </select>
            <p className="mg-hint">Intense sweating and muscular turnover elevate electrolyte clearance.</p>
          </label>

          <div className="mg-field-row">
            <label className="mg-field">
              <span className="mg-field-label">Stress (0-10)</span>
              <select value={stressScore} onChange={(e) => setStressScore(e.target.value)}>
                {[...Array(11).keys()].map((i) => (
                  <option key={i} value={i}>
                    {i} {i >= 7 ? '(High)' : i <= 3 ? '(Low)' : '(Moderate)'}
                  </option>
                ))}
              </select>
            </label>
            <label className="mg-field">
              <span className="mg-field-label">Sleep (Hours)</span>
              <select value={sleepHours} onChange={(e) => setSleepHours(e.target.value)}>
                <option value="9">9+ hrs</option>
                <option value="8">8 hrs</option>
                <option value="7">7 hrs</option>
                <option value="6">6 hrs (Short)</option>
                <option value="5">≤ 5 hrs (Deprived)</option>
              </select>
            </label>
          </div>

          {/* Diet */}
          <div className="mg-section-label">
            <span>Dietary Intake</span>
            <small style={{ fontWeight: 400, color: 'var(--mg-muted)', textTransform: 'none' }}>Food &amp; Water</small>
          </div>

          <div className="mg-field-row" style={{ marginBottom: '8px' }}>
            <label className="mg-checkbox-label">
              <input
                type="radio"
                name="dietMode"
                checked={dietMode === 'preset'}
                onChange={() => setDietMode('preset')}
              />
              Estimate by Quality
            </label>
            <label className="mg-checkbox-label">
              <input
                type="radio"
                name="dietMode"
                checked={dietMode === 'custom'}
                onChange={() => setDietMode('custom')}
              />
              Enter Tracked mg
            </label>
          </div>

          {dietMode === 'preset' ? (
            <label className="mg-field">
              <select value={dietPreset} onChange={(e) => setDietPreset(e.target.value)}>
                <option value="">-- Select Dietary Quality --</option>
                <option value="low">Low (~160 mg) · Few seeds, greens, or nuts</option>
                <option value="medium">Moderate (~270 mg) · Some greens and whole grains</option>
                <option value="high">High (~420 mg) · Plentiful pumpkin seeds, spinach, legumes</option>
              </select>
            </label>
          ) : (
            <label className="mg-field">
              <span className="mg-field-label">Estimated Food Magnesium (mg/day)</span>
              <input
                type="number"
                min="0"
                max="1500"
                value={customDietMg}
                onChange={(e) => setCustomDietMg(e.target.value)}
                placeholder="e.g. 260"
              />
            </label>
          )}

          {/* Clinical Factors */}
          <div className="mg-section-label">
            <span>Medications &amp; Health</span>
            <small style={{ fontWeight: 400, color: 'var(--mg-muted)', textTransform: 'none' }}>Absorption</small>
          </div>

          <label className="mg-checkbox-label">
            <input
              type="checkbox"
              checked={ppiUse}
              onChange={(e) => setPpiUse(e.target.checked)}
            />
            Chronic PPI use (Omeprazole, Pantoprazole) (−10% absorption)
          </label>

          <label className="mg-checkbox-label">
            <input
              type="checkbox"
              checked={diureticUse}
              onChange={(e) => setDiureticUse(e.target.checked)}
            />
            Diuretic use (Loop or Thiazides) (−10% urinary loss)
          </label>

          <label className="mg-checkbox-label">
            <input
              type="checkbox"
              checked={giMalabsorption}
              onChange={(e) => setGiMalabsorption(e.target.checked)}
            />
            Malabsorption condition (Celiac, Crohn’s, Gastric bypass) (−20%)
          </label>

          <label className="mg-checkbox-label" style={{ color: '#b91c1c', fontWeight: 600 }}>
            <input
              type="checkbox"
              checked={kidneyImpairment}
              onChange={(e) => setKidneyImpairment(e.target.checked)}
            />
            Known Chronic Kidney Impairment (CKD)
          </label>

          {/* Supplements */}
          <div className="mg-section-label">
            <span>Supplements</span>
            <small style={{ fontWeight: 400, color: 'var(--mg-muted)', textTransform: 'none' }}>UL: 350 mg</small>
          </div>

          <label className="mg-checkbox-label">
            <input
              type="checkbox"
              checked={takesSupplement}
              onChange={(e) => setTakesSupplement(e.target.checked)}
            />
            I currently take a magnesium supplement
          </label>

          {takesSupplement && (
            <div style={{ animation: 'fadeIn 0.2s ease' }}>
              <div className="mg-field-row">
                <label className="mg-field">
                  <span className="mg-field-label">Elemental Mg (mg)</span>
                  <input
                    type="number"
                    min="1"
                    max="1200"
                    value={supplementDoseMg}
                    onChange={(e) => setSupplementDoseMg(e.target.value)}
                    placeholder="e.g. 200"
                  />
                </label>
                <label className="mg-field">
                  <span className="mg-field-label">Frequency</span>
                  <select
                    value={supplementFrequency}
                    onChange={(e) => setSupplementFrequency(e.target.value)}
                  >
                    <option value="daily">Daily</option>
                    <option value="2x_day">Twice Daily</option>
                    <option value="alternate_day">Every Other Day</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </label>
              </div>
              <p className="mg-hint">
                Check your bottle for <em>elemental</em> magnesium per dose (e.g. 200 mg in 1,000 mg magnesium glycinate).
              </p>
            </div>
          )}
        </div>

        {/* Right Column: Results & Analytics */}
        <div className="mg-results">
          {results.error ? (
            <div className="mg-panel mg-result-hero">
              <div className="mg-result-top">
                <div>
                  <p className="mg-eyebrow">02 / Assessment</p>
                  <h2>Daily Magnesium Status</h2>
                </div>
                <span className={`mg-badge ${results.isAwaiting ? 'mg-badge-orange' : 'mg-badge-red'}`}>
                  {results.isAwaiting ? 'Awaiting Input' : 'Incomplete Input'}
                </span>
              </div>
              <div style={{ padding: '16px 0 6px' }}>
                <p style={{ fontSize: '1rem', color: 'var(--mg-text)', margin: '0 0 8px' }}>
                  {results.error}
                </p>
                <p style={{ fontSize: '0.85rem', color: 'var(--mg-muted)', margin: 0 }}>
                  Enter your age and biological sex in the panel on the left to calculate your personalized daily intake, effective dietary absorption, and safety limits.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Primary Assessment Hero Card with Auto-Adjusting Badge */}
              <section className="mg-panel mg-result-hero">
                <div className="mg-result-top">
                  <div>
                    <p className="mg-eyebrow">02 / Assessment</p>
                    <h2>Daily Magnesium Status</h2>
                  </div>

                  {/* Dynamic Badge auto-adjusting to Green, Orange, or Red */}
                  <span className={`mg-badge ${results.badgeClass}`}>
                    <span style={{ fontSize: '0.8em' }}>●</span> {results.badgeLabel}
                  </span>
                </div>

                <div className="mg-metrics-grid">
                  <div className="mg-metric-cell">
                    <span className="mg-metric-label">Personalized Target</span>
                    <div className="mg-metric-val">
                      {results.targetMg}
                      <span> mg/day</span>
                    </div>
                    <div className="mg-metric-sub">
                      Base DRI {results.isAI ? 'AI' : 'RDA'}: {results.baseRda} mg/day
                    </div>
                  </div>

                  <div className="mg-metric-cell">
                    <span className="mg-metric-label">Total Estimated Intake</span>
                    <div className="mg-metric-val">
                      {results.totalIntake}
                      <span> mg/day</span>
                    </div>
                    <div className="mg-metric-sub">
                      {results.gap > 0 ? (
                        <span style={{ color: 'var(--mg-orange)' }}>
                          Deficit: ~{results.gap} mg/day below target
                        </span>
                      ) : (
                        <span style={{ color: 'var(--mg-green)' }}>
                          Surplus: +{Math.abs(results.gap)} mg/day above target
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Visual Progress Bar */}
                <div className="mg-progress-wrap">
                  <div className="mg-progress-track">
                    <div
                      className="mg-progress-fill-diet"
                      style={{
                        width: `${Math.min(100, (results.effectiveDietary / (results.targetMg || 400)) * 100)}%`,
                      }}
                      title={`Effective Diet: ${results.effectiveDietary} mg`}
                    />
                    <div
                      className="mg-progress-fill-supp"
                      style={{
                        width: `${Math.min(
                          Math.max(0, 100 - (results.effectiveDietary / (results.targetMg || 400)) * 100),
                          (results.dailySupplementMg / (results.targetMg || 400)) * 100
                        )}%`,
                      }}
                      title={`Supplements: ${results.dailySupplementMg} mg`}
                    />
                  </div>
                  <div className="mg-progress-legend">
                    <span>
                      <span className="mg-dot mg-dot-diet" /> Dietary Mg (Effective): <b>{results.effectiveDietary} mg</b>
                    </span>
                    <span>
                      <span className="mg-dot mg-dot-supp" /> Supplemental Mg: <b>{results.dailySupplementMg} mg</b>
                    </span>
                  </div>
                </div>

                {/* Quick Stats Row */}
                <div className="mg-stats">
                  <div>
                    <small>Food Intake (Raw)</small>
                    <strong>{results.rawDietary} mg</strong>
                  </div>
                  <div>
                    <small>Absorption Factor</small>
                    <strong>{Math.round(results.absorptionFactor * 100)}%</strong>
                  </div>
                  <div>
                    <small>Supplemental UL (Max)</small>
                    <strong>{MG_SUPPLEMENTAL_UL} mg/day</strong>
                  </div>
                </div>
              </section>

              {/* Safety Warnings & Clinical Flags */}
              {kidneyImpairment && (
                <div className="mg-error-notice">
                  <strong>CRITICAL SAFETY ALERT:</strong> You selected known kidney impairment. The kidneys are the primary route for magnesium excretion. Excess intake can cause life-threatening hypermagnesemia. <strong>Do not take magnesium supplements without physician supervision.</strong>
                </div>
              )}

              {!kidneyImpairment && results.dailySupplementMg > MG_SUPPLEMENTAL_UL && (
                <div className="mg-warning-notice">
                  <strong>SUPPLEMENTAL UPPER LIMIT EXCEEDED:</strong> Your supplemental elemental magnesium (~{results.dailySupplementMg} mg/day) exceeds the Tolerable Upper Intake Level (UL) of <strong>350 mg/day</strong>. High supplemental doses commonly cause osmotic diarrhea and cramping. (Note: UL does not apply to dietary food magnesium).
                </div>
              )}

              {/* Analytical Breakdown Table (Isolated mg-table) */}
              <section className="mg-panel mg-module">
                <div className="mg-module-heading">
                  <p className="mg-eyebrow">03 / Analytics</p>
                  <h2>Intake &amp; Absorption Breakdown</h2>
                  <p>Scientific adjustments based on your medications and lifestyle factors.</p>
                </div>

                <div className="mg-table-wrap">
                  <table className="mg-table">
                    <thead>
                      <tr>
                        <th>Component</th>
                        <th>Value</th>
                        <th>Status / Factor</th>
                        <th>Clinical Mechanism</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>Base Dietary Requirement</strong></td>
                        <td>{results.baseRda} mg/day</td>
                        <td><span className="mg-badge mg-badge-green">DRI Standard</span></td>
                        <td>Baseline RDA or AI for age {age} and sex ({sex}).</td>
                      </tr>
                      <tr>
                        <td><strong>Personalized Target</strong></td>
                        <td>{results.targetMg} mg/day</td>
                        <td>
                          {activityLevel !== 'low' || parseFloat(stressScore) >= 7 ? (
                            <span className="mg-badge mg-badge-orange">Loss Adjusted</span>
                          ) : (
                            <span className="mg-badge mg-badge-green">Baseline</span>
                          )}
                        </td>
                        <td>Includes exercise sweat losses (+10-20%) and stress excretion (+5%).</td>
                      </tr>
                      <tr>
                        <td><strong>Raw Food Intake</strong></td>
                        <td>{results.rawDietary} mg/day</td>
                        <td>Dietary Estimate</td>
                        <td>Naturally occurring magnesium from food and mineral water.</td>
                      </tr>
                      <tr>
                        <td><strong>Effective Food Mg</strong></td>
                        <td>{results.effectiveDietary} mg/day</td>
                        <td>{Math.round(results.absorptionFactor * 100)}% Bioavailable</td>
                        <td>Adjusted for PPIs, diuretics, and malabsorptive GI conditions.</td>
                      </tr>
                      <tr>
                        <td><strong>Elemental Supplemental Mg</strong></td>
                        <td>{results.dailySupplementMg} mg/day</td>
                        <td>
                          {results.dailySupplementMg > MG_SUPPLEMENTAL_UL ? (
                            <span className="mg-badge mg-badge-red">Above UL</span>
                          ) : results.dailySupplementMg > 0 ? (
                            <span className="mg-badge mg-badge-green">Within UL</span>
                          ) : (
                            'None'
                          )}
                        </td>
                        <td>Max safe supplemental daily dose: 350 mg elemental Mg.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Actionable Nutrition Strategies */}
              <section className="mg-panel mg-module">
                <div className="mg-module-heading">
                  <p className="mg-eyebrow">04 / Strategy</p>
                  <h2>Evidence-Based Recommendations</h2>
                </div>

                <div className="mg-cards-grid">
                  <div className="mg-card">
                    <h4>Top Magnesium-Rich Foods</h4>
                    <p className="mg-card-desc">
                      Increase whole-food density with:
                    </p>
                    <ul style={{ margin: '8px 0 0', paddingLeft: '18px', fontSize: '0.82rem', color: 'var(--mg-text)' }}>
                      <li><strong>Pumpkin Seeds (Pepitas):</strong> 1 oz (28g) ≈ 156 mg</li>
                      <li><strong>Cooked Spinach:</strong> ½ cup ≈ 78 mg</li>
                      <li><strong>Chia Seeds:</strong> 1 oz (28g) ≈ 111 mg</li>
                      <li><strong>Almonds / Cashews:</strong> 1 oz ≈ 80 mg</li>
                      <li><strong>Black Beans:</strong> ½ cup cooked ≈ 60 mg</li>
                      <li><strong>Dark Chocolate (70%+):</strong> 1 oz ≈ 64 mg</li>
                    </ul>
                  </div>

                  <div className="mg-card">
                    <h4>Bioavailability &amp; Forms</h4>
                    <p className="mg-card-desc">
                      If bridging a deficit with supplements:
                    </p>
                    <ul style={{ margin: '8px 0 0', paddingLeft: '18px', fontSize: '0.82rem', color: 'var(--mg-text)' }}>
                      <li><strong>Magnesium Glycinate:</strong> Highly bioavailable, gentle on the stomach, ideal for evening relaxation.</li>
                      <li><strong>Magnesium Malate:</strong> Great for daytime cellular energy and muscle recovery.</li>
                      <li><strong>Magnesium Citrate:</strong> Well absorbed; can have mild osmotic laxative properties.</li>
                      <li><strong>Magnesium Oxide:</strong> Poorly absorbed (~4%); mainly used for constipation.</li>
                    </ul>
                  </div>
                </div>
              </section>
            </>
          )}
        </div>
      </div>

      {/* Complete Educational Reference Guide */}
      <div id="mg-education" className="mg-guide-section">
        <div className="mg-module-heading">
          <p className="mg-eyebrow">Educational Reference</p>
          <h2>Understanding Magnesium Biochemistry &amp; Health</h2>
          <p>Scientific mechanisms, absorption pathways, and clinical reference standards.</p>
        </div>

        {/* 1. Essential Roles Grid */}
        <div className="mg-def-grid">
          <div className="mg-def-card">
            <h4>1. Cellular Energy (ATP Production)</h4>
            <p>
              Magnesium is a required cofactor for adenosine triphosphate (ATP). Biologically active ATP is bound as an <strong>Mg-ATP complex</strong>. Without sufficient intracellular magnesium, cellular respiration and glucose metabolism cannot operate at full capacity.
            </p>
          </div>

          <div className="mg-def-card">
            <h4>2. Neuromuscular Contraction &amp; Cramps</h4>
            <p>
              Magnesium acts as a natural physiological calcium channel blocker. While calcium triggers muscle fiber contraction, magnesium enables muscle relaxation and inhibits excessive nerve firing through GABA and NMDA receptor modulation.
            </p>
          </div>

          <div className="mg-def-card">
            <h4>3. Bone Structure &amp; Vitamin D Activation</h4>
            <p>
              Approximately 50-60% of total body magnesium resides within the bone matrix. Furthermore, all enzymes metabolizing Vitamin D into its active form (25(OH)D and 1,25(OH)2D) require magnesium as a cofactor.
            </p>
          </div>

          <div className="mg-def-card">
            <h4>4. Blood Pressure &amp; Endothelial Health</h4>
            <p>
              Magnesium promotes vascular smooth muscle relaxation, stimulating endothelial prostacyclin and nitric oxide synthesis, which supports healthy arterial dilation and normal blood pressure regulation.
            </p>
          </div>
        </div>

        {/* 2. Reference Categories Table */}
        <div className="mg-ref-grid">
          <div className="mg-ref-block">
            <h4>Recommended Dietary Allowances (RDA)</h4>
            <ul className="mg-ref-list">
              <li><span>Adult Men (19-30 yrs)</span> <strong>400 mg/day</strong></li>
              <li><span>Adult Men (31-50+ yrs)</span> <strong>420 mg/day</strong></li>
              <li><span>Adult Women (19-30 yrs)</span> <strong>310 mg/day</strong></li>
              <li><span>Adult Women (31-50+ yrs)</span> <strong>320 mg/day</strong></li>
              <li><span>Pregnancy</span> <strong>350-360 mg/day</strong></li>
              <li><span>Lactation</span> <strong>310-320 mg/day</strong></li>
            </ul>
          </div>

          <div className="mg-ref-block">
            <h4>Clinical Risk Factors for Inadequacy</h4>
            <ul className="mg-ref-list">
              <li><span>Chronic PPI Use (Omeprazole)</span> <strong>Impaired TRPM6 channel uptake</strong></li>
              <li><span>Loop &amp; Thiazide Diuretics</span> <strong>Elevated renal excretion</strong></li>
              <li><span>Endurance Athletes / Sweating</span> <strong>+10-20% higher requirement</strong></li>
              <li><span>Chronic Stress &amp; Cortisol</span> <strong>Elevated urinary magnesium clearance</strong></li>
              <li><span>Type 2 Diabetes</span> <strong>Glucosuria-induced magnesium loss</strong></li>
            </ul>
          </div>
        </div>

        {/* Clinical Sources */}
        <div className="mg-sources">
          <strong>Scientific Methodologies &amp; Clinical Sources:</strong>
          <br />
          • National Institutes of Health, Office of Dietary Supplements. <em>Magnesium Fact Sheet for Health Professionals</em>.
          <br />
          • Institute of Medicine (US) Standing Committee on the Scientific Evaluation of Dietary Reference Intakes. (1997). <em>Dietary Reference Intakes for Calcium, Phosphorus, Magnesium, Vitamin D, and Fluoride</em>. Washington (DC): National Academies Press.
          <br />
          • Nielsen, F. H., &amp; Lukaski, H. C. (2006). Update on the relationship between magnesium and exercise. <em>Magnesium Research</em>, 19(3), 180-189.
          <br />
          • Hess, M. W., et al. (2012). Hypomagnesaemia from proton-pump inhibitors: an overview. <em>Alimentary Pharmacology &amp; Therapeutics</em>, 36(5), 405-413.
          <br />
          • US Food and Drug Administration (FDA). (2011). <em>FDA Drug Safety Communication: Low magnesium levels can be associated with long-term use of Proton Pump Inhibitors</em>.
        </div>
      </div>
    </div>
  );
}

export default MagnesiumCalculator;